import { css } from '@emotion/react';
import { Typography } from '@mui/material';
import { useAtomValue, useSetAtom } from 'jotai';
import { FC, useEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router';
import { GenericFallbacksWrapper } from '../../../components/Fallback/GenericFallbacksWrapper';
import { useSyncListsFromParams } from '../../../components/Layout/Navbar/Navigation/hooks/useSyncListsFromParams';
import { ContainerName } from '../../../constants/styles';
import { activeCompanyListIdStateJ, listsStateLoadableJ } from '../../../services/state/ListsStateJ';
import { CompanyList2 } from '../Companies2/CompanyList2';
import { CreateListCompanyModal } from '../Companies3/ListForms2/NewListCompanyForm';
import { ErrorOccurredMessage } from '../components/Messages/HelpMessage';
import { usePrepItemsData } from '../hooks/usePrepItemsData';
import { ListExportButton } from './ActionButtons/ListExportButton';
import { ListUploadButtons } from './ActionButtons/ListUploadButtons';
import { CompanyListSkeletonLoader } from './CompanyListSkeletonLoader';
import { FiltersAndViewSettings } from './FiltersAndViewSettings/FiltersAndViewSettings';
import { ListContainerRef } from './state/ListsUiState';

const Container = css`
  width: 100%;
  container-name: ${ContainerName.companyLists};
  container-type: inline-size;
  margin: 1rem 2rem;
  display: grid;
  grid-template-rows: min-content min-content 1fr;
`;

const HeadingWrapper = css`
  height: 4.5rem;
  display: grid;
  grid-template-columns: repeat(2, auto);
  width: 100%;
  align-items: center;
  justify-content: space-between;
`;

const ButtonsWrapper = css`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 1rem;
  justify-content: end;
  justify-items: end;
`;

export function CompanyListLoader() {
  useSyncListsFromParams();
  return (
    <>
      <CreateListCompanyModal />
      <Outlet />
    </>
  );
}

export function Companies() {
  const containerRef = useRef<HTMLDivElement>(null);
  const setContainerRef = useSetAtom(ListContainerRef);

  useEffect(() => {
    setContainerRef(containerRef);
  }, [containerRef, setContainerRef]);

  return (
    <div css={Container} ref={containerRef}>
      <CompaniesContent />
    </div>
  );
}

export const CompaniesContent: FC = () => {
  const companyLists = useAtomValue(listsStateLoadableJ);
  const activeListId = useAtomValue(activeCompanyListIdStateJ);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { ready, error } = usePrepItemsData(activeListId);

  if (companyLists.state === 'hasError' || error) {
    return <ErrorOccurredMessage />;
  }

  if (companyLists.state !== 'hasData' || !ready) {
    return <CompanyListSkeletonLoader />;
  }

  const lists = companyLists.data ?? [];
  const activeList = activeListId != null ? lists.find((l) => l.id === activeListId) : null;

  return (
    <>
      <div css={HeadingWrapper}>
        <Typography variant='h4'>{activeList?.name ?? ''}</Typography>
        <div css={ButtonsWrapper}>
          <ListUploadButtons />
          <ListExportButton />
        </div>
      </div>
      <FiltersAndViewSettings />
      <GenericFallbacksWrapper suspenseFallback={<CompanyListSkeletonLoader />}>
        <CompanyList2 setSelectedIndex={setSelectedIndex} selectedIndex={selectedIndex} />
      </GenericFallbacksWrapper>
    </>
  );
};
